import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, EMPTY, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { IdentityService } from 'projects/bis-shared/src/lib/auth/identity.service';
import { Router } from '@angular/router';
import { EnvironmentService } from '../environment/environment.service';
import { AppUrls } from '../utility/appurls';
import { IdleService } from '../utility/idle.service';
import { ExternalApiUrls } from '../utility/apiurls-external';
import { InternalApiUrls } from '../utility/apiurls-internal';

@Injectable({
  providedIn: 'root',
})
export class HttpTokenInterceptorService implements HttpInterceptor {
  constructor(
    private identityService: IdentityService,
    private router: Router,
    private environmentService: EnvironmentService,
    private idleService: IdleService,
    private internalApiUrls: InternalApiUrls,
    private externalApiUrls: ExternalApiUrls,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    //Allow cookie to be received on login call, send hash for refresh token
    if (
      req.url ===
      (this.environmentService.isExternalSite
        ? this.externalApiUrls.user.login()
        : this.internalApiUrls.user.login())
    ) {
      let setHeaders: { [name: string]: string | string[] };
      if (this.identityService.apiToken) {
        setHeaders = {
          'bis-api-security': this.identityService.apiToken.apiSecurityHash || 'null',
        };
      }
      return next.handle(
        req.clone({
          withCredentials: true,
          setHeaders: setHeaders,
        }),
      );
    }

    // Only intercept calls that are to our configured APIs
    const config = this.environmentService.getEnvironment();
    const urlsToIntercept = Object.values(config.baseUrls);
    const interceptCall: boolean = urlsToIntercept.some(
      url => req.url.startsWith(url) || req.url.startsWith(window.document.location.origin + url),
    );
    if (!interceptCall) {
      return next.handle(req);
    }

    if (this.identityService.isTokenValid()) {
      // Token is not expired, continue by adding token headers
      return next.handle(this.addTokenHeaders(req)).pipe(
        catchError((err: HttpErrorResponse) => {
          const authenticateRespHeader = err.headers.get('www-authenticate');
          if (authenticateRespHeader?.startsWith('Bearer error="invalid_token"')) {
            return this.waitForTokenRefreshAndContinue(req, next, true);
          }
          return throwError(err);
        }),
      );
    }

    if (this.idleService.userHasTimedOut()) {
      //Don't bother attempting authenticated calls if the user has timed out
      return throwError('User has timed out.');
    }

    return this.waitForTokenRefreshAndContinue(req, next);
  }

  waitForTokenRefreshAndContinue(
    req: HttpRequest<any>,
    next: HttpHandler,
    forceLogin?: boolean,
  ): Observable<HttpEvent<any>> {
    return this.identityService.refreshTokenAndIdentity(forceLogin).pipe(
      switchMap(() => next.handle(this.addTokenHeaders(req))),
      catchError(err => {
        if (err?.url === req.url) {
          //Let errors sourced from the original URL flow through
          return throwError(err);
        } else {
          // refreshToken doesn't have an expiration that we're given, so we will always try with that first
          // if the refreshToken returns an error, then it's no longer valid so we'll display an idle logout
          this.router.navigate([AppUrls.logout()], {
            queryParams: { idle: true, state: 'showIdleLogoutWarning' },
          });
          return EMPTY;
        }
      }),
    );
  }

  addTokenHeaders(request: HttpRequest<any>): HttpRequest<any> {
    const newRequest = request.clone({
      withCredentials: true,
      setHeaders: {
        Authorization: `Bearer ${this.identityService.apiToken.accessToken}`,
        'bis-api-security': this.identityService.apiToken.apiSecurityHash || 'null',
      },
    });

    return newRequest;
  }
}
